// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import {
  X, ShieldCheck, Power, Maximize, Minimize, Settings, Key,
  LayoutDashboard, Wallet, Radar, LineChart, BrainCircuit,
  Zap, Crosshair, BookOpen, ListOrdered, PieChart, Users, Activity,
  Mic, Clock, Scale, Target, ChevronLeft, ChevronRight, Star, Database,
  Menu, Home, BarChart3, RefreshCw
} from 'lucide-react';

// ── PAGE IMPORTS — EAGER vs LAZY (God 2026-09-04, mobile blueprint Phase 1) ──
// Everything used to be a static import, so the build emitted ONE 2,947 kB
// chunk (812 kB gzip) and a phone downloaded and parsed the entire console —
// Backtest, Accounts, the Pine editor, xlsx — before it could draw four index
// cards. That single chunk is the biggest mobile cost in the app and it is not
// free on desktop either.
//
// EAGER = the shell and what the first screen actually needs. Everything else
// is React.lazy and arrives when its tab is opened. Named exports need the
// .then(m => ({ default: m.X })) shim; SuperChart, MamtaAiv6 and Watchlist are
// default exports and do not.
import { Login } from './Login';
import { LiveTrading } from './LiveTrading';
// M-H6 (KALI mobile audit 2026-10-04): the marketing site is only for signed-out visitors and
// the Dashboard is no longer the admin landing tab (LiveAutoTrading is), so neither belongs in
// the entry chunk every phone downloads first.
const Website         = React.lazy(() => import('./Website').then(m => ({ default: m.Website })));
const Dashboard       = React.lazy(() => import('./Dashboard').then(m => ({ default: m.Dashboard })));
import { useViewport } from './hooks/useBreakpoint';
import { useMarketCalendar, venueHoliday } from './hooks/useMarketCalendar';
import { effectivePerms, fetchMe, PAGES as ACCESS_PAGES } from './lib/access';

const MamtaScreener   = React.lazy(() => import('./MamtaScreener').then(m => ({ default: m.MamtaScreener })));
const SymbolManager   = React.lazy(() => import('./SymbolManager').then(m => ({ default: m.SymbolManager })));
const SuperChart      = React.lazy(() => import('./components/SuperChart'));
const ApiManagement   = React.lazy(() => import('./ApiManagement').then(m => ({ default: m.ApiManagement })));
const PnLReports      = React.lazy(() => import('./PnLReports').then(m => ({ default: m.PnLReports })));
const RnDAnalytics    = React.lazy(() => import('./RnDAnalytics').then(m => ({ default: m.RnDAnalytics })));
const PipelinePanel   = React.lazy(() => import('./components/PipelinePanel'));
const ClientPortfolio = React.lazy(() => import('./ClientPortfolio').then(m => ({ default: m.ClientPortfolio })));
const UserControl     = React.lazy(() => import('./UserControl').then(m => ({ default: m.UserControl })));
const MoneyManagement = React.lazy(() => import('./MoneyManagement').then(m => ({ default: m.MoneyManagement })));
const MamtaAiv6       = React.lazy(() => import('./MamtaAiv6'));
const PaperTrading    = React.lazy(() => import('./PaperTrading').then(m => ({ default: m.PaperTrading })));
const OrderLine       = React.lazy(() => import('./OrderLine').then(m => ({ default: m.OrderLine })));
const PositionBook    = React.lazy(() => import('./PositionBook').then(m => ({ default: m.PositionBook })));
const AccountsAdmin   = React.lazy(() => import('./AccountsAdmin').then(m => ({ default: m.AccountsAdmin })));
const OptionsChain    = React.lazy(() => import('./OptionsChain').then(m => ({ default: m.OptionsChain })));
const Watchlist       = React.lazy(() => import('./Watchlist'));

// ── ONE-CLICK BRIDGE TOKEN CAPTURE (God UX 2026-08-02) ──────────────────────
// Terminal PC par site ko `#bridge_token=<token>` ke saath kholo — token
// localStorage me store, hash turant URL se wipe. Hash fragment browser se
// bahar KABHI nahi jaata (server/logs ko nahi milta) — isliye safe carrier.
// Isse Das-Mahavidya local swarm strip/cards bina F12-console ke live ho
// jaate hain (DrillDown/GodApproval/Notebooks sab yahi key padhte hain).
try {
  if (window.location.hash.startsWith('#bridge_token=')) {
    const tok = decodeURIComponent(window.location.hash.slice('#bridge_token='.length));
    if (tok) localStorage.setItem('mamta_bridge_token', tok);
    history.replaceState(null, '', window.location.pathname + window.location.search);
  }
} catch { /* storage blocked = non-fatal; panels will show honest OFFLINE */ }

// ── GOD-GATE TOKEN AUTO-ALIGN (God 2026-08-04, approved Layer-2) ────────────
// localStorage is PER-ORIGIN — a token linked on one domain (web.app) does NOT
// carry to another (www.jaiswalcapital.com), so the God-gate threw a recurring
// "error (401): unauthorized" popup. Self-align by fetching the current token
// from the AWS bridge (CORS-gated to our allowlisted origins only). Every
// origin now just works — no manual paste, restart-proof. NOTE the /bridge/bridge
// path is CORRECT: nginx `location /bridge/` strips one prefix while the app's own
// route is /bridge/session_token. Fail-soft: a blocked/failed fetch is non-fatal
// and panels fall back to the manually pasted token.
try {
  fetch('https://api.jaiswalcapital.com/bridge/bridge/session_token', { headers: { accept: 'application/json' } })
    .then(r => (r.ok ? r.json() : null))
    .then(d => { if (d && d.token) localStorage.setItem('mamta_bridge_token', d.token); })
    .catch(() => { /* bridge offline / blocked = non-fatal */ });
} catch { /* non-fatal */ }

// ⏱️ OPTIMIZED CLOCK COMPONENT (Isse poora app har second re-render nahi hoga aur performance badhegi)
const LiveClock = () => {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const timeString = time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const dateString = time.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'short' });

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <span style={{ fontSize: '14px', fontWeight: 900, color: '#e2e8f0', letterSpacing: '1px', fontFamily: 'monospace' }}>{timeString}</span>
      <span style={{ fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px' }}>{dateString}</span>
    </div>
  );
};

const SESSION_KEY = 'jc_auth_session';
const _readSession = () => { try { const s = localStorage.getItem(SESSION_KEY); return s ? JSON.parse(s) : null; } catch { return null; } };

// ⚠️ TEMPORARY DEV AUTH BYPASS — remove before shipping.
// Hard-gated to `import.meta.env.DEV` so it is dead code in any production
// build (vite tree-shakes the false branch). Lets local `npm run dev` jump
// straight to the dashboard as ADMIN to preview the new Mamta Screener UI.
// Disable without code edits by setting VITE_AUTH_BYPASS=0 in a .env file.
const _DEV_AUTH_BYPASS = import.meta.env.DEV && import.meta.env.VITE_AUTH_BYPASS !== '0';
const _bootSession = () => _DEV_AUTH_BYPASS
  ? { role: 'ADMIN' as const, id: 'dev-bypass' }
  : _readSession();

// DIRECT-OPEN LANDING (God 2026-09-05): the app opens straight into the trading
// console instead of Dashboard — "drops the user directly into the action".
// It is a function of ROLE, not a constant, and that is load-bearing:
// LiveAutoTrading is allowedRoles ['ADMIN','STAFF'] and its render guard is
// `userRole !== 'CLIENT'`, so defaulting a CLIENT there would land them on a
// blank surface with no page mounted. CLIENT (and a not-yet-known role) keeps
// Dashboard. An explicit ?page= deep-link still overrides this everywhere.
const _landingTab = (role: 'ADMIN' | 'STAFF' | 'CLIENT' | null) =>
  role === 'ADMIN' || role === 'STAFF' ? 'LiveAutoTrading' : 'Dashboard';

// Did this request actually CARRY a session bearer? (God 2026-09-05, reported
// from a fully-working console wearing a red "Admin session expired" banner.)
// The 401 detector below used to fire on ANY 401 from the API, but several
// endpoints 401 BY DESIGN without the session ever being touched:
//   • /admin/tv-token authenticates with X-Admin-Key and sends no bearer at all
//   • Login's /admin/audit mirror runs BEFORE a token exists, so it sends "Bearer "
// Both are harmless and both were being reported as a dead admin session. A 401
// only means "your session expired" if a real token was presented and refused —
// so an empty "Bearer " must NOT count, which is why \S+ is required.
const _carriedBearer = (args: any[]): boolean => {
  try {
    const h = args?.[1]?.headers
      ?? (args?.[0] && typeof args[0] !== 'string' ? args[0].headers : null);
    if (!h) return false;
    let v = '';
    if (typeof h.get === 'function') v = h.get('Authorization') || h.get('authorization') || '';
    else if (Array.isArray(h)) {
      const f = h.find((p: any) => String(p?.[0]).toLowerCase() === 'authorization');
      v = f ? f[1] : '';
    } else v = h.Authorization || h.authorization || '';
    return /^Bearer\s+\S+/.test(String(v || ''));
  } catch { return false; }
};

// Both chart tabs are full-screen surfaces: they hide the app header, suppress
// the mobile bottom bar and own their own scrolling. Written once so the KALI
// chart cannot end up with half the paper chart's layout rules (God 2026-08-26).
const IS_CHART_TAB = (t: string) => t === 'SuperChart' || t === 'KaliSuperChart';

export default function App() {
  // DIRECT-OPEN FIX (God 2026-09-05, reported from the phone): a `?page=` in the
  // URL means "take me to this app surface" — the PWA start_url and the /m
  // handoff both carry one. Without this guard an unauthenticated open fell to
  // <Website/>, the PUBLIC MARKETING SITE: light-themed, no GOD identity and no
  // logout. That is precisely the "dark theme nahi aa rahi / god aur logout ka
  // option nahi hai" report — the app was not broken, the phone was being shown
  // the wrong surface. A deep-link goes to Login (dark, #020202) and on through
  // to the console. A bare visit to the root still gets the website as before.
  const [showWebsite, setShowWebsite] = useState(() => {
    try {
      if (new URLSearchParams(window.location.search).get('page')) return false;
    } catch { /* ignore */ }
    return !_bootSession()?.role;
  });
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!_bootSession()?.role);
  const [userRole, setUserRole] = useState<'ADMIN' | 'STAFF' | 'CLIENT' | null>(() => _bootSession()?.role ?? null);
  const [loggedUserId, setLoggedUserId] = useState<string>(() => _bootSession()?.id ?? '');
  const [authExpired, setAuthExpired] = useState(false);   // durable fix A: surface expired admin token
  // ACCESS LAW (God 2026-09-24): the server's answer to "who am I and what may I see"
  // (/auth/me). The menu AND the page render both obey it; the server enforces the
  // same grants on every write, so hiding is not the only wall.
  const [me, setMe] = useState<any>(null);

  // Dev bypass lands directly on the new Mamta Screener; normal boot uses Dashboard.
  const [activeTab, setActiveTab] = useState(() => {
    // Deep-link support: /?page=SuperChart (used by the mobile dashboard's nav).
    try {
      const p = new URLSearchParams(window.location.search).get('page');
      if (p) return p;
    } catch { /* ignore */ }
    return _DEV_AUTH_BYPASS ? 'MamtaScreener' : _landingTab(_bootSession()?.role ?? null);
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    if (!isAuthenticated || _DEV_AUTH_BYPASS) { setMe(null); return; }
    let alive = true;
    const pull = () => fetchMe('https://api.jaiswalcapital.com').then(j => {
      if (!alive || !j) return;                       // unreachable: keep the last answer
      if (j.authenticated === false || j.relogin) { setAuthExpired(true); setMe(j); return; }
      setMe(j);
      try {
        const cur = JSON.parse(localStorage.getItem('jc_my_profile') || 'null') || {};
        localStorage.setItem('jc_my_profile', JSON.stringify({ ...cur, role: j.role, permissions: j.permissions }));
      } catch { /* storage blocked */ }
    });
    pull();
    const id = setInterval(pull, 120000);            // a grant or block by the Master lands within 2 min
    return () => { alive = false; clearInterval(id); };
  }, [isAuthenticated]);
  // Landing / deep link on a page this user may not open -> first page they may open.
  useEffect(() => {
    if (!me || !me.permissions || me.role === 'MASTER_ADMIN') return;
    const ids = ACCESS_PAGES.map(p => p.id);
    if (!ids.includes(activeTab) || me.permissions[activeTab]?.view) return;
    const pref = ['LiveAutoTrading', 'Dashboard', 'SuperChart', 'PositionBook', 'MoneyManagement', ...ids];
    const first = pref.find(id => me.permissions[id]?.view);
    if (first) setActiveTab(first);
  }, [me, activeTab]);
  // AUTO UP-DOWN (God 2026-08-30): the sidebar scrollbar is hidden, so the
  // menu must position itself — whenever the active tab changes, glide its
  // item into view. 'nearest' never yanks the list when it is already visible.
  useEffect(() => {
    try {
      document.querySelector('[data-jc-active="1"]')
        ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    } catch { /* non-scrollable layout — nothing to do */ }
  }, [activeTab]);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [chartParams, setChartParams] = useState<{ symbol: string; model: string; tf?: string }>({ symbol: 'NIFTY 50', model: 'VEG' });
  const [chartKey, setChartKey] = useState(0);

  // One shared listener for the whole console (blueprint Phase 2). This used to
  // be a local useState + its own resize handler, repeated in thirteen files
  // with three different thresholds.
  // A phone turned sideways on a chart page is still a phone: no desktop sidebar or
  // header around the chart - the chart itself goes full-screen (God 2026-09-24).
  const _vp = useViewport();
  const isMobile = _vp.isMobile || (_vp.isLandPhone && IS_CHART_TAB(activeTab));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // UPDATE READY (God 2026-09-15, iOS audit). main.tsx used to reload the tab
  // the moment it saw a newer bundle - on an iPhone that is every time the app
  // comes back from the background, which could tear down an armed hold or an
  // open sheet. It now announces instead; this pill is the user's reload.
  const [updateReady, setUpdateReady] = useState<boolean>(() => !!(window as any).__jcUpdateReady);
  useEffect(() => {
    const h = () => setUpdateReady(true);
    window.addEventListener('jc-update-ready', h);
    return () => window.removeEventListener('jc-update-ready', h);
  }, []);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('jc_sb_collapsed') === '1'; } catch { return false; }
  });

  // DURABLE FIX A (God 2026-08-04): global admin-auth-expiry detector. The
  // cross-domain admin token (jc_auth_token) has a 24h TTL; when it expires,
  // every API admin write 401s while the app still looks "logged in". Wrap fetch
  // ONCE to catch any 401 from the API and surface a re-login banner — so an
  // expired session never fails silently across pages. Pure UX; gate unchanged.
  useEffect(() => {
    const orig = window.fetch;
    let last = 0;
    window.fetch = async (...args: any[]) => {
      const res = await orig(...(args as [any, any?]));
      try {
        const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
        if (res && res.status === 401 && /api\.jaiswalcapital\.com/.test(String(url))
            && _carriedBearer(args)) {
          const now = Date.now();
          if (now - last > 8000) { last = now; window.dispatchEvent(new CustomEvent('jc-auth-expired')); }
        }
      } catch { /* never break a fetch */ }
      return res;
    };
    return () => { window.fetch = orig; };
  }, []);
  useEffect(() => {
    const h = () => { if (userRole) setAuthExpired(true); };   // only when we thought we were logged in
    window.addEventListener('jc-auth-expired', h);
    return () => window.removeEventListener('jc-auth-expired', h);
  }, [userRole]);

  // SuperChart ka mobile bottom nav (sc-mbar) apna ☰ se app sidebar kholta hai —
  // wahan App ka bottom bar hidden hota hai, isliye ye event bridge.
  useEffect(() => {
    const h = () => setSidebarOpen(true);
    window.addEventListener('jc-open-sidebar', h);
    return () => window.removeEventListener('jc-open-sidebar', h);
  }, []);

  // Mobile "Back" from full-screen pages (SuperChart hides the app header, so
  // its sc-mbar Back chip fires this event). Remembers the last normal tab.
  const prevTabRef = useRef('Dashboard');
  useEffect(() => {
    if (!IS_CHART_TAB(activeTab) && activeTab !== 'OrderLine') prevTabRef.current = activeTab;
  }, [activeTab]);
  useEffect(() => {
    const h = () => { setActiveTab(prevTabRef.current || 'Dashboard'); setSidebarOpen(false); };
    window.addEventListener('jc-nav-back', h);
    return () => window.removeEventListener('jc-nav-back', h);
  }, []);

  // REAL BACK STACK (God 2026-09-05: "mobile app me back jaane ka option nahi").
  // `prevTabRef` above only remembers the last NON-chart tab, so on a normal page
  // it equals the CURRENT tab and "back" would land on itself. The console also
  // switches tabs without touching browser history, and the /m handoff uses
  // location.replace on purpose (location.href would bounce straight back into
  // the redirect), so neither the phone's back gesture nor any in-app control
  // could return anywhere. Keep the actual trail of visited tabs instead.
  const tabTrailRef = useRef<string[]>([]);
  const lastTabRef = useRef(activeTab);
  // depth is STATE, not derived from the ref: a ref mutation does not re-render,
  // so reading tabTrailRef.current.length during render showed the Back button one
  // navigation late (the effect pushes AFTER the render that would have shown it).
  const [backDepth, setBackDepth] = useState(0);
  useEffect(() => {
    if (lastTabRef.current !== activeTab) {
      tabTrailRef.current.push(lastTabRef.current);
      if (tabTrailRef.current.length > 20) tabTrailRef.current.shift();
      lastTabRef.current = activeTab;
      setBackDepth(tabTrailRef.current.length);
    }
  }, [activeTab]);
  const canGoBack = backDepth > 0;
  const goBack = () => {
    const t = tabTrailRef.current.pop();
    if (!t) return;
    lastTabRef.current = t;      // set BEFORE the state change so the effect above does not re-push
    setBackDepth(tabTrailRef.current.length);
    setActiveTab(t);
    setSidebarOpen(false);
  };

  // ── Shared header smart pills: NSE OPEN/CLOSED + countdown, DHAN feed dot ──
  const [, setHdrTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setHdrTick(x => x + 1), 30_000); return () => clearInterval(t); }, []);
  const [dhanFeed, setDhanFeed] = useState<{ state: string; flowing: boolean } | null>(null);
  useEffect(() => {
    let dead = false;
    const poll = async () => {
      try {
        const r = await fetch('https://api.jaiswalcapital.com/scanner/feed-status', { signal: AbortSignal.timeout(6000) });
        if (!dead && r.ok) { const d = await r.json(); setDhanFeed({ state: d.state || '?', flowing: d.ltp_flowing === true }); }
      } catch { if (!dead) setDhanFeed(null); }
    };
    poll();
    const t = setInterval(() => { if (!document.hidden) poll(); }, 30_000);   // 🔁 hidden-pause
    return () => { dead = true; clearInterval(t); };
  }, []);
  // ── LIVE INDEX PRICES in the header (God 2026-08-13) ──────────────────────
  // Reads /scanner/live-prices, which serves the spine's Redis mirror. RULE #22
  // single-ingest: the browser never touches Dhan. Those Redis keys carry a 10s
  // TTL, so a key that exists is at most ~10s old and a dead feed empties the
  // namespace — which is why this renders '—' on !fresh instead of holding the
  // last number it saw. A stale price shown as live is the one thing this must
  // never do.
  const [livePx, setLivePx] = useState<{ fresh: boolean; idx: Record<string, number> } | null>(null);
  const prevPxRef = useRef<Record<string, number>>({});
  useEffect(() => {
    let dead = false;
    const poll = async () => {
      try {
        const r = await fetch('https://api.jaiswalcapital.com/scanner/live-prices', { signal: AbortSignal.timeout(6000) });
        if (dead || !r.ok) return;
        const d = await r.json();
        if (d?.ok) {
          setLivePx(prev => {
            if (prev?.idx) prevPxRef.current = prev.idx;
            return { fresh: d.fresh === true, idx: d.indices || {} };
          });
        }
      } catch { /* keep the last render; `fresh` still governs what is shown */ }
    };
    poll();
    const t = setInterval(() => { if (!document.hidden) poll(); }, 5_000);
    return () => { dead = true; clearInterval(t); };
  }, []);

  // ── MULTI-EXCHANGE TIME STATE MACHINE (God Phase-8, 2026-08-01) ──
  // NSE/BSE: 09:00 PRE-OPEN (no trading) → 09:15 LIVE cash → 15:30 cash HALT
  // → 15:40 F&O extended HALT → CLOSED. MCX: 09:00 → 23:30 (23:55 Nov-Mar,
  // US-DST link) → CLOSED. IST-normalized (browser-local tz ignored).
  // Backend twin: tools/mamta-bridge/exchange_clock.py — rules MUST match.
  // God 2026-10-20 (Dussehra): exchange holidays come from the backend calendar
  // (modules/market_session.calendar_view) - the local clock below cannot know them.
  const mktCal = useMarketCalendar();
  const mktInfo = (() => {
    const ist = new Date(Date.now() + 5.5 * 3600 * 1000);
    const d = ist.getUTCDay(); const mins = ist.getUTCHours() * 60 + ist.getUTCMinutes();
    const month = ist.getUTCMonth() + 1;
    const weekend = (d === 0 || d === 6);
    const left = (m: number) => `${Math.floor(m / 60)}h ${m % 60}m`;
    const CLOSED = { open: false, state: 'CLOSED', txt: 'CLOSED', color: '#ef4444' };
    const nse = (() => {
      if (weekend) return CLOSED;
      if (mins < 9 * 60) return { open: false, state: 'CLOSED', txt: `OPENS ${left(9 * 60 - mins)}`, color: '#ef4444' };
      if (mins < 9 * 60 + 15) return { open: false, state: 'PRE-OPEN', txt: 'PRE-OPEN · sync only', color: '#f59e0b' };
      if (mins < 15 * 60 + 30) return { open: true, state: 'LIVE', txt: `LIVE · ${left(15 * 60 + 30 - mins)}`, color: '#10b981' };
      if (mins < 15 * 60 + 40) return { open: true, state: 'F&O EXT', txt: 'F&O till 15:40', color: '#fb923c' };
      return CLOSED;
    })();
    const HOLIDAY = (name: string) => ({ open: false, state: 'HOLIDAY', txt: 'MARKET CLOSED - HOLIDAY', color: '#ef4444', holiday: name });
    const eq = (venue: 'NSE' | 'BSE') => {
      const hol = venueHoliday(mktCal, venue);
      return hol?.full ? HOLIDAY(hol.name) : nse;
    };
    // MCX close from the backend (US-DST exact, God 2026-10-20); month guess only as fallback.
    const _mc = mktCal?.timings?.MCX?.close;
    const mcxClose = _mc ? parseInt(_mc.slice(0, 2), 10) * 60 + parseInt(_mc.slice(3, 5), 10)
      : ((month >= 11 || month <= 3) ? 23 * 60 + 55 : 23 * 60 + 30);
    const mcx = (() => {
      if (weekend) return CLOSED;
      const hol = venueHoliday(mktCal, 'MCX');
      if (hol?.full) return HOLIDAY(hol.name);
      const p = hol?.v.holiday_partial;
      const split = p ? (parseInt(p.split.slice(0, 2), 10) * 60 + parseInt(p.split.slice(3, 5), 10)) : 17 * 60;
      if (hol?.partialMorning && mins < split)
        return { open: false, state: 'HOLIDAY', txt: `HOLIDAY · EVE OPENS ${left(split - mins)}`, color: '#f59e0b', holiday: hol.name };
      if (hol?.partialEvening && mins >= split) return HOLIDAY(hol.name);
      if (mins < 9 * 60) return { open: false, state: 'CLOSED', txt: `OPENS ${left(9 * 60 - mins)}`, color: '#ef4444' };
      if (mins <= mcxClose) return { open: true, state: 'LIVE', txt: `LIVE · ${left(mcxClose - mins)}`, color: '#10b981' };
      return CLOSED;
    })();
    return {
      open: eq('NSE').open,
      exchanges: [
        { name: 'NSE', ...eq('NSE') },
        { name: 'BSE', ...eq('BSE') },
        { name: 'MCX', ...mcx },
      ],
    };
  })();

  // ── Profile photo upload (localStorage, har page ke shared header me) ──
  const [avatarUrl, setAvatarUrl] = useState<string | null>(() => { try { return localStorage.getItem('jc_avatar'); } catch { return null; } });

  // Master-admin display name + photo — read from the User-Control store so a name/
  // photo change there reflects in this header (it used to be a hardcoded name).
  // God 2026-09-04: the fallback name is "Subhas Prasad" — Subhas, no trailing 'h'.
  const [adminProfile, setAdminProfile] = useState<{ name: string; photo?: string }>(() => {
    try { const a = JSON.parse(localStorage.getItem('jaiswal_system_users') || '[]').find((u: any) => u.role === 'MASTER_ADMIN'); if (a?.name) return { name: a.name, photo: a.photo }; } catch {}
    return { name: 'Subhas Prasad' };
  });
  useEffect(() => {
    const load = () => { try { const a = JSON.parse(localStorage.getItem('jaiswal_system_users') || '[]').find((u: any) => u.role === 'MASTER_ADMIN'); if (a?.name) setAdminProfile({ name: a.name, photo: a.photo }); } catch {} };
    load();
    fetch('https://api.jaiswalcapital.com/admin/users', { credentials: 'include', headers: { 'Authorization': 'Bearer ' + (localStorage.getItem('jc_auth_token') || '') } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { const a = d?.users?.find((u: any) => u.role === 'MASTER_ADMIN'); if (a?.name) setAdminProfile({ name: a.name, photo: a.photo }); })
      .catch(() => {});
    window.addEventListener('jc_admin_updated', load);
    return () => window.removeEventListener('jc_admin_updated', load);
  }, []);
  const onAvatarPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; if (!f) return;
    const img = new Image();
    img.onload = () => {
      const c = document.createElement('canvas'); const S = 96; c.width = S; c.height = S;
      const ctx = c.getContext('2d')!;
      const m = Math.min(img.width, img.height);
      ctx.drawImage(img, (img.width - m) / 2, (img.height - m) / 2, m, m, 0, 0, S, S);
      const url = c.toDataURL('image/jpeg', 0.85);
      try { localStorage.setItem('jc_avatar', url); } catch {}
      setAvatarUrl(url);
    };
    img.src = URL.createObjectURL(f);
    e.target.value = '';
  };

  const handleOpenChart = (symbol: string, model: string = 'VEG', tf?: string) => {
    setChartParams({ symbol, model, tf });
    setChartKey(k => k + 1);
    setActiveTab('SuperChart');
  };

  // GOD APPROVALS → SuperChart one-click jump (God 2026-08-18).
  // The approvals deck is lazy-loaded three levels down (ReportsAnalytics →
  // GodApprovalDeck), so a window event beats threading a callback through every
  // layer — the deck stays independent of App's shape, and any other panel that
  // wants "open this cell's chart" gets it for free.
  useEffect(() => {
    const onOpenChart = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      if (!d.symbol) return;
      handleOpenChart(String(d.symbol), d.model || 'VEG', d.tf ? String(d.tf) : undefined);
    };
    window.addEventListener('mamta:open-chart', onOpenChart as EventListener);
    return () => window.removeEventListener('mamta:open-chart', onOpenChart as EventListener);
  }, []);

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => console.warn(err));
    } else {
      if (document.exitFullscreen) document.exitFullscreen();
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleLoginSuccess = (role: 'ADMIN' | 'STAFF' | 'CLIENT', id: string) => {
    try { localStorage.setItem(SESSION_KEY, JSON.stringify({ role, id })); } catch {}
    setIsAuthenticated(true);
    setUserRole(role);
    setLoggedUserId(id);
    // Direct-open: land in the console the moment the login clears, so the
    // handoff from /m does not stop on Dashboard when the session had lapsed.
    setActiveTab(_landingTab(role));
  };

  const handleLogout = () => {
    try {
      const sid = localStorage.getItem('jc_session_id');
      if (sid) fetch('https://api.jaiswalcapital.com/admin/sessions/terminate', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + (localStorage.getItem('jc_auth_token') || '') },
        body: JSON.stringify({ sessionId: sid }),
      }).catch(() => {});
      localStorage.removeItem('jc_session_id');
      localStorage.removeItem('jc_auth_token');
      localStorage.removeItem(SESSION_KEY);
    } catch {}
    setIsAuthenticated(false);
    setUserRole(null);
    setShowWebsite(true);
  };

  // 🔴 Phase 3 — REAL session heartbeat. Pings the server every 60s; if an admin
  // terminated this session (or it expired) the server says terminated:true and we
  // log out. Powers the User-Control "Live Sessions" tab + remote kill.
  useEffect(() => {
    if (!isAuthenticated) return;
    const beat = () => {
      const sid = localStorage.getItem('jc_session_id');
      if (!sid) return;
      fetch('https://api.jaiswalcapital.com/admin/sessions/heartbeat', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ sessionId: sid }),
      }).then(r => r.ok ? r.json() : null)
        .then(d => { if (d?.terminated) { try { alert('🔴 Session terminated by admin. Dobara login karo.'); } catch {} handleLogout(); } })
        .catch(() => {});
    };
    const t = setInterval(beat, 60_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // 🛡 SYSTEM IDLE LOCK — User Control me set minutes tak koi activity na ho
  // (mouse/touch/key/scroll) to auto-logout. Khula terminal = khula trading
  // account; ye usse protect karta hai. 0 / missing = off.
  useEffect(() => {
    if (!isAuthenticated) return;
    let last = Date.now();
    const bump = () => { last = Date.now(); };
    const evs = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'visibilitychange'];
    evs.forEach(e => window.addEventListener(e, bump, { passive: true }));
    const t = setInterval(() => {
      // God 2026-07-29: Master Admin (ADMIN) gets a full-day 12h window so continuous
      // live-dashboard monitoring isn't broken by the 30-min lock; standard users /
      // clients keep the 30-min security lock unchanged (role-scoped exemption).
      const mins = userRole === 'ADMIN' ? 720 : parseInt(localStorage.getItem('jc_idle_timeout_mins') || '30');
      if (!mins || !Number.isFinite(mins)) return;
      if (Date.now() - last > mins * 60_000) {
        handleLogout();
        try { alert(`🛡 Security: ${mins} min inactivity — session auto-locked. Dobara login karo.`); } catch {}
      }
    }, 30_000);
    return () => { evs.forEach(e => window.removeEventListener(e, bump)); clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, userRole]);

  if (showWebsite && !isAuthenticated) {
    return (
      <React.Suspense fallback={<div style={{ height: '100dvh', background: 'var(--jc-ground)' }} />}>
        <Website onLoginClick={() => setShowWebsite(false)} />
      </React.Suspense>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="lg-root" style={{ position: 'relative', width: '100vw', height: '100dvh', backgroundColor: 'var(--jc-ground)' }}>
        {!isMobile && (
          <button
            onClick={() => setShowWebsite(true)}
            style={{ position: 'absolute', top: '14px', left: '160px', zIndex: 50, backgroundColor: 'rgba(19,23,34,.8)', color: '#cbd5e1', border: '1px solid #2a2e39', padding: '6px 14px', borderRadius: '8px', fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase', cursor: 'pointer', backdropFilter: 'blur(8px)' }}
          >
            ← Website
          </button>
        )}
        <Login onLoginSuccess={handleLoginSuccess} />
      </div>
    );
  }

  const menuItems = [
    { id: 'Dashboard', label: 'DASHBOARD', icon: LayoutDashboard, allowedRoles: ['ADMIN', 'STAFF', 'CLIENT'] },
    { id: 'AccountsAdmin', label: 'ACCOUNTS & AUDIT', icon: Scale, allowedRoles: ['ADMIN'] }, 
    { id: 'MoneyManagement', label: 'FUNDS & MARGIN', icon: Wallet, allowedRoles: ['ADMIN', 'STAFF', 'CLIENT'] },
    { id: 'MamtaScreener', label: 'MAMTA SCREENER', icon: Radar, allowedRoles: ['ADMIN', 'STAFF'] },
    // The two charts name their own lane (God 2026-08-26). A chart that does
    // not say which lane it is on is a chart you can fire the wrong signal from.
    { id: 'SuperChart', label: 'DUMABATI SUPER CHART', sub: '\u{1F9EA} PAPER TRADING', subColor: '#38bdf8',
      icon: LineChart, allowedRoles: ['ADMIN', 'STAFF', 'CLIENT'] },
    // KALI's OWN chart (God 2026-08-26). Two charts, two webhooks, zero shared
    // path: this one fires to /kali/webhook/tv and may only fire LIVE bots,
    // while SUPER CHART above stays paper. ADMIN-only, because a signal fired
    // from here is a real-money request.
    { id: 'KaliSuperChart', label: '\u{1F531} KALI SUPER CHART', sub: '\u{1F534} LIVE TRADING', subColor: '#f87171',
      icon: LineChart, allowedRoles: ['ADMIN'] },
    { id: 'Watchlist', label: 'WATCHLIST', icon: Star, allowedRoles: ['ADMIN', 'STAFF', 'CLIENT'] },
    { id: 'OptionsChain', label: 'OPTIONS CHAIN', icon: Target, allowedRoles: ['ADMIN', 'STAFF'] },
    { id: 'MamtaAiPro', label: 'MAMTA AI PRO', icon: BrainCircuit, allowedRoles: ['ADMIN'] },
    // The two auto-trading pages name their lane first (God 2026-08-26).
    { id: 'LiveAutoTrading', label: 'PAPER AUTO TRADING', sub: '\u{1F9EA} DUMABATI', subColor: '#38bdf8',
      icon: Zap, allowedRoles: ['ADMIN', 'STAFF'] },
    // KALI sits directly under DUMABATI so the pair reads as a pair, and is
    // ADMIN-only: this page can disarm real-money cells.
    { id: 'Kali', label: '\u{1F531} LIVE AUTO TRADING', sub: '\u{1F534} KALI \u00B7 REAL MONEY', subColor: '#f87171',
      icon: Zap, allowedRoles: ['ADMIN'] },
    // God 2026-09-09: a generated Pine script looks finished the moment it
    // exists on disk. This page is where its UNFINISHED business lives — the
    // gate it passed on, the paper-test day it is on, and the audit it still
    // owes. ADMIN-only: it is the approval ledger, and RULE #17 keeps the final
    // audit with the Master Admin.
    { id: 'Pipeline', label: '\u{1F6E1} GOD APPROVAL / PIPELINE', sub: 'LIFECYCLE TRACKER', subColor: '#818cf8',
      icon: ShieldCheck, allowedRoles: ['ADMIN'] },
    { id: 'PaperTrading', label: 'PAPER TRADING', icon: Activity, allowedRoles: ['ADMIN', 'STAFF'] },
    { id: 'PositionBook', label: 'POSITION BOOK', icon: BookOpen, allowedRoles: ['ADMIN', 'STAFF', 'CLIENT'] },
    { id: 'OrderLine', label: 'ORDER LINE', icon: ListOrdered, allowedRoles: ['ADMIN', 'STAFF', 'CLIENT'] },
    { id: 'PnlReports', label: 'PNL REPORTS', icon: PieChart, allowedRoles: ['ADMIN', 'STAFF', 'CLIENT'] },
    { id: 'RnDAnalytics', label: 'R&D ANALYTICS', icon: Clock, allowedRoles: ['ADMIN', 'STAFF'] },
    { id: 'ClientPortfolio', label: 'CLIENT PORTFOLIO', icon: Users, allowedRoles: ['ADMIN', 'STAFF'] },
    { id: 'SymbolManager', label: 'SYMBOL MANAGER', icon: Database, allowedRoles: ['ADMIN'] },
    { id: 'UserControl', label: 'USER CONTROL', icon: Settings, allowedRoles: ['ADMIN'] },
    { id: 'ApiManagement', label: 'API MANAGEMENT', icon: Key, allowedRoles: ['ADMIN'] },
  ];

  // PER-USER PAGE ENFORCEMENT (God 2026-08-30). Role decides the CEILING;
  // the admin's per-user page approvals (Master Control, 13-page matrix)
  // decide what actually renders. Rules, stated so nobody re-derives them:
  //   * MASTER_ADMIN / ADMIN role: matrix never restricts the master.
  //   * Matrix EMPTY (legacy user, pre-matrix): role default — never lock a
  //     working user out because their record predates the feature.
  //   * Page NOT in the 13-page matrix (Watchlist etc.): role default.
  //   * Page IN the matrix: needs .view approved by the admin.
  // Same jc_my_profile drives every device — approvals travel with the login.
  // PLAIN READ, deliberately NOT a hook (HOTFIX 2026-08-30): this line sits
  // BELOW the `if (!authed) return <Login/>` early-return, so a useMemo here
  // changed the hook count the moment login flipped — minified React #310,
  // the full-screen crash God screenshotted. A ~1KB JSON.parse per render is
  // nothing; a conditional hook is a crash.
  const _myPerms: Record<string, any> = (() => {
    try {
      const p = JSON.parse(localStorage.getItem('jc_my_profile') || 'null');
      return (p && p.permissions) || {};
    } catch { return {}; }
  })();
  // ACCESS LAW (God 2026-09-24, replaces the 2026-08-30 rules above): only MASTER_ADMIN
  // is unrestricted. Everyone else - role ADMIN included - sees exactly the pages the
  // Master granted; a page missing from the grant is DENIED (it used to fall back to the
  // role default, so 8 of 21 pages could never be taken away). Source: /auth/me (server
  // truth); until it answers, the profile the login returned, with the same law applied.
  const _myRole: string = String((me && me.role) || (() => {
    try { return JSON.parse(localStorage.getItem('jc_my_profile') || 'null')?.role || ''; } catch { return ''; }
  })() || '').toUpperCase();
  const _isMaster = _DEV_AUTH_BYPASS || _myRole === 'MASTER_ADMIN';
  const _eff: Record<string, any> | null = _isMaster ? null
    : (me && me.permissions) ? me.permissions
    : _myRole ? effectivePerms(_myRole, _myPerms) : null;
  const canOpen = (id: string) => {
    const item = menuItems.find(m => m.id === id);
    if (!item) return true;                                  // not a menu page (e.g. internal views)
    if (!item.allowedRoles.includes(userRole || 'CLIENT')) return false;
    if (_isMaster) return true;
    if (!_eff) return true;   // identity not known yet / server unreachable: role default (every write is still checked server-side)
    return !!_eff[id]?.view;
  };
  const permittedMenus = menuItems.filter(item => canOpen(item.id));
  const activeMenuTitle = menuItems.find(m => m.id === activeTab)?.label || activeTab;

  const getProfileData = () => {
    if (userRole === 'ADMIN') {
      const nm = adminProfile.name || 'Subhas Prasad';
      return { name: nm, avatar: adminProfile.photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(nm)}&background=4f46e5&color=fff&rounded=true&bold=true`, roleDisplay: 'MASTER ADMIN' };
    }
    // NON-MASTER IDENTITY (God 2026-08-30): the header shows the logged-in
    // user's OWN server profile — real name + uploaded photo — on every
    // device, instead of a generic "System Desk" placeholder.
    try {
      const me = JSON.parse(localStorage.getItem('jc_my_profile') || 'null');
      if (me?.name) {
        return {
          name: me.name,
          avatar: me.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(me.name)}&background=0f172a&color=10b981&rounded=true&bold=true`,
          roleDisplay: String(me.role || 'STAFF').replace('_', ' '),
        };
      }
    } catch { /* fall through to placeholders */ }
    if (userRole === 'STAFF') {
      return { name: 'System Desk', avatar: 'https://ui-avatars.com/api/?name=System+Desk&background=0f172a&color=10b981&rounded=true&bold=true', roleDisplay: 'STAFF ACCOUNT' };
    } else {
      return { name: loggedUserId || 'Client User', avatar: `https://ui-avatars.com/api/?name=${loggedUserId || 'C'}&background=0f172a&color=3b82f6&rounded=true&bold=true`, roleDisplay: 'ACTIVE CLIENT' };
    }
  };

  const profile = getProfileData();

  // Dashboard uses lowercase snake_case IDs; map them to App activeTab IDs
  const DASH_NAV_MAP: Record<string, string> = {
    dashboard:        'Dashboard',
    super_chart:      'SuperChart',
    option_chain:     'OptionsChain',
    options_chain:    'OptionsChain',
    mamta_screener:   'MamtaScreener',
    live_trading:     'LiveAutoTrading',
    mamta_ai:         'MamtaAiPro',
    position_book:    'PositionBook',
    money_management: 'MoneyManagement',
  };

  const navTo = (id: string) => {
    setActiveTab(id);
    if (isMobile) setSidebarOpen(false);
  };

  const dashboardNav = (id: string, symbol?: string) => {
    // Dashboard click with a symbol (Market Movers / Sector stock) → open that
    // scrip's chart, not the default NIFTY.
    if (symbol && (id === 'super_chart' || DASH_NAV_MAP[id] === 'SuperChart')) {
      handleOpenChart(symbol);
      if (isMobile) setSidebarOpen(false);
      return;
    }
    navTo(DASH_NAV_MAP[id] ?? id);
  };

  return (
    <div style={{ display: 'flex', height: '100dvh', width: '100vw', backgroundColor: 'var(--jc-ground)', color: 'var(--jc-ink)', overflow: 'hidden', fontFamily: 'var(--jc-ui)' }}>
      {authExpired && (
        <div className="jc-auth-banner" style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 99999, background: '#b91c1c', color: '#fff',
                      padding: '9px 16px', display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center',
                      fontSize: 13.5, fontWeight: 600, boxShadow: '0 2px 14px rgba(0,0,0,.45)', flexWrap: 'wrap' }}>
          <span>🔒 Admin session expired — please log in again to continue admin actions.</span>
          <button onClick={() => { try { localStorage.removeItem('jc_auth_token'); localStorage.removeItem('jc_session_id'); } catch { /* storage blocked */ } setAuthExpired(false); setUserRole(null); setIsAuthenticated(false); setShowWebsite(false); }}
            style={{ background: '#fff', color: '#b91c1c', border: 'none', borderRadius: 6, padding: '5px 16px', fontWeight: 700, cursor: 'pointer' }}>Log in again</button>
          <button onClick={() => setAuthExpired(false)} title="dismiss for now"
            style={{ background: 'transparent', color: '#fecaca', border: '1px solid #fecaca', borderRadius: 6, padding: '5px 12px', cursor: 'pointer' }}>Dismiss</button>
        </div>
      )}
      {/* 📱 Fixed bottom nav — quick page shortcuts ("Mobile View" button removed).
          SuperChart par YE bar hidden hai: wahan chart ka apna bottom nav hai
          (sc-mbar) jiska ☰ 'jc-open-sidebar' event se yahi sidebar kholta hai. */}
      {/* UPDATE-READY PILL (God 2026-09-15, iOS audit). Sits above the bottom
          nav so a thumb can reach it; tapping it is the ONLY way a new build
          reloads while the console is in use. */}
      {isMobile && updateReady && (
        <button onClick={() => { (window as any).__jcMarkUserReload?.(); window.location.reload(); }} aria-label="New version ready - tap to update"
          className="jc-press"
          style={{ position: 'fixed', left: '50%', transform: 'translateX(-50%)',
            bottom: `calc(${IS_CHART_TAB(activeTab) ? 104 : 70}px + env(safe-area-inset-bottom))`, zIndex: 99998,
            display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', minHeight: 40,
            borderRadius: 999, border: '1px solid rgba(86,184,255,0.45)',
            background: 'rgba(7,10,18,0.92)', color: 'var(--jc-info)', fontSize: 11, fontWeight: 800,
            letterSpacing: .3, boxShadow: '0 8px 28px rgba(0,0,0,0.55)',
            WebkitBackdropFilter: 'blur(14px)', backdropFilter: 'blur(14px)', cursor: 'pointer' }}>
          <RefreshCw size={14} /> New version ready · tap to update
        </button>
      )}
      {isMobile && !IS_CHART_TAB(activeTab) && activeTab !== 'OrderLine' && (
        /* 2026 glass nav (God 2026-09-04). It is translucent on purpose: the
           content scrolling under it is the cue that the bar floats rather
           than ends the page. The blur is what keeps the labels legible over
           whatever colour happens to pass beneath.
           ICONS (God 2026-09-15, iOS audit): Lucide glyphs replace the emoji.
           Emoji render as whatever the OS ships - a different weight and
           colour on every iOS version, unreadable through the grayscale
           filter - and carried no accessible name. These are vector, take the
           tab colour, and every button now says what it is to VoiceOver. */
        <nav aria-label="Primary" ref={el => {
          // Publish the nav's real height (safe area included) so page-level bars can
          // sit ON it instead of under it (God 2026-09-24: the KILL/DEPLOY bar was hidden).
          if (!el || (el as any).__jcRo || typeof ResizeObserver === 'undefined') return;
          const ro = new ResizeObserver(() => document.documentElement.style.setProperty('--jc-mnav-h', `${Math.round(el.getBoundingClientRect().height)}px`));
          ro.observe(el); (el as any).__jcRo = ro;
        }} style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 99999,
          display: 'flex', alignItems: 'stretch', gap: 3,
          padding: '6px 8px calc(6px + env(safe-area-inset-bottom))',
          background: 'linear-gradient(180deg, rgba(11,15,26,0.82) 0%, rgba(7,10,18,0.94) 100%)',
          borderTop: '1px solid var(--jc-hairline)',
          boxShadow: '0 -8px 28px rgba(0,0,0,0.45)',
          WebkitBackdropFilter: 'blur(18px) saturate(1.3)',
          backdropFilter: 'blur(18px) saturate(1.3)' }}>
          {([
            { id: '__menu',          label: 'Menu',   Icon: Menu,      aria: 'Open menu' },
            { id: 'Dashboard',       label: 'Home',   Icon: Home,      aria: 'Dashboard' },
            { id: 'SuperChart',      label: 'Chart',  Icon: LineChart, aria: 'Super Chart (paper)' },
            { id: 'LiveAutoTrading', label: 'Paper',  Icon: Zap,       aria: 'Paper auto trading' },
            { id: 'PositionBook',    label: 'Posn',   Icon: BookOpen,  aria: 'Position book' },
            { id: 'PnlReports',      label: 'P&L',    Icon: BarChart3, aria: 'P&L reports' },
          ] as const)
            .filter(b => b.id === '__menu' || permittedMenus.some(m => m.id === b.id))
            .map(b => {
              const active = activeTab === b.id;
              return (
            <button key={b.id}
              className="jc-press"
              aria-label={b.aria}
              aria-current={active ? 'page' : undefined}
              onClick={() => b.id === '__menu' ? setSidebarOpen(o => !o) : navTo(b.id)}
              style={{ flex: 1, minHeight: 48, position: 'relative',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', gap: 3, borderRadius: 'var(--jc-r-md)',
                cursor: 'pointer', transition: 'background 140ms ease, color 140ms ease',
                border: active ? '1px solid rgba(86,184,255,0.30)' : '1px solid transparent',
                background: active ? 'var(--jc-info-soft)' : 'transparent',
                color: active ? 'var(--jc-info)' : 'var(--jc-text-mute)',
                fontSize: 9, fontWeight: 800, letterSpacing: .4, textTransform: 'uppercase',
                WebkitTapHighlightColor: 'transparent' }}>
              {/* A lit rail on the ACTIVE tab. An icon tint alone is easy to
                  miss on a dark bar at a glance. */}
              {active && (
                <span style={{ position: 'absolute', top: 3, left: '28%', right: '28%', height: 2,
                  borderRadius: 99, background: 'var(--jc-info)',
                  boxShadow: '0 0 10px rgba(86,184,255,0.65)' }} />
              )}
              <b.Icon size={19} strokeWidth={active ? 2.4 : 2} aria-hidden="true" />
              {b.label}
            </button>
              );
            })}
        </nav>
      )}

      {/* Mobile backdrop */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 90, backdropFilter: 'blur(2px)' }}
        />
      )}

      {/* SIDEBAR */}
      {(() => { const collapsed = !isMobile && sidebarCollapsed; return (
      <div className={isMobile ? 'jc-sidebar-phone' : undefined} style={{
        width: isMobile ? '260px' : collapsed ? '52px' : '220px',
        backgroundColor: '#050505',
        borderRight: '1px solid #1e293b',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        zIndex: 100,
        overflow: 'hidden',
        transition: isMobile ? 'none' : 'width 0.22s cubic-bezier(0.4,0,0.2,1)',
        ...(isMobile ? {
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100dvh',
          transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.28s cubic-bezier(0.4,0,0.2,1)',
          boxShadow: sidebarOpen ? '4px 0 32px rgba(0,0,0,0.8)' : 'none',
        } : {})
      }}>

        {/* BRANDING */}
        <div style={{ height: '60px', display: 'flex', alignItems: 'center', gap: collapsed ? 0 : '12px', padding: collapsed ? '0' : '0 16px', justifyContent: collapsed ? 'center' : 'flex-start', borderBottom: '1px solid #1e293b', flexShrink: 0 }}>
          <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'linear-gradient(135deg, #4f46e5, #1e40af)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Activity size={18} color="white" />
          </div>
          {!collapsed && (
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: '14px', fontWeight: 900, color: 'white', letterSpacing: '2px', textTransform: 'uppercase', lineHeight: '1.2' }}>JAISWAL</span>
              <span style={{ fontSize: '10px', fontWeight: 900, color: '#818cf8', letterSpacing: '2px', textTransform: 'uppercase' }}>Capital</span>
            </div>
          )}
          {isMobile && (
            <button onClick={() => setSidebarOpen(false)} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}>
              <X size={20} />
            </button>
          )}
        </div>

        {/* MENU ITEMS — bar hidden (jc-side-scroll); the menu positions ITSELF:
            active item is kept in view automatically, so the long scrollbar
            line God flagged has no job left (2026-08-30). */}
        <div className="jc-side-scroll" style={{ flex: 1, overflowY: 'auto', padding: collapsed ? '8px 6px' : '12px 10px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {!collapsed && <div style={{ fontSize: '9px', fontWeight: 900, color: '#334155', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '6px', paddingLeft: '8px' }}>Master Menu</div>}
          {permittedMenus.map((item) => {
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                data-jc-active={isActive ? '1' : undefined}
                onClick={() => navTo(item.id)}
                title={collapsed ? item.label : undefined}
                style={{
                  display: 'flex', alignItems: 'center', gap: collapsed ? 0 : '10px',
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  padding: collapsed ? '10px 0' : '10px 12px',
                  borderRadius: '10px', width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer',
                  backgroundColor: isActive ? '#4f46e5' : 'transparent',
                  color: isActive ? '#ffffff' : '#94a3b8',
                  fontWeight: 800, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.8px',
                  transition: 'background 0.15s ease',
                  whiteSpace: 'nowrap',
                }}
              >
                <item.icon size={15} color={isActive ? '#ffffff' : '#64748b'} />
                {!collapsed && (
                  <span style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>
                    {'sub' in item && (item as any).sub && (
                      <span style={{ fontSize: 8, fontWeight: 900, letterSpacing: 0.6, lineHeight: 1.1,
                        color: isActive ? 'rgba(255,255,255,.85)' : ((item as any).subColor || '#64748b') }}>
                        {(item as any).sub}
                      </span>
                    )}
                  </span>
                )}
              </button>
            )
          })}
        </div>

        {/* BOTTOM ACTIONS */}
        <div style={{ padding: collapsed ? '10px 6px' : '12px 10px', borderTop: '1px solid #1e293b', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {/* WHO AM I + LOGOUT (God 2026-09-05). The only logout used to be a bare
              ⏻ icon in the header, and on a phone it was pushed off-screen. The
              ☰ menu is where a phone user looks for it, and a LABELLED button is
              findable in a way an unlabelled icon is not. */}
          {!collapsed && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', borderRadius: '10px', background: 'rgba(129,140,248,.06)', border: '1px solid rgba(129,140,248,.16)' }}>
                <ShieldCheck size={13} color={userRole === 'ADMIN' ? '#818cf8' : '#60a5fa'} style={{ flexShrink: 0 }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '10.5px', fontWeight: 900, color: '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{profile.name}</div>
                  <div style={{ fontSize: '8.5px', fontWeight: 900, letterSpacing: '.5px', textTransform: 'uppercase', color: userRole === 'ADMIN' ? '#818cf8' : '#60a5fa' }}>{profile.roleDisplay}</div>
                </div>
              </div>
              <button onClick={handleLogout} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', backgroundColor: 'rgba(239,68,68,.10)', border: '1px solid rgba(239,68,68,.32)', color: '#f87171', padding: '10px', borderRadius: '10px', fontSize: '10.5px', fontWeight: 900, letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' }}>
                <Power size={13} /> Logout
              </button>
            </>
          )}
          {!collapsed && (
            <button onClick={toggleFullScreen} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', backgroundColor: '#0f172a', border: '1px solid #1e293b', color: '#94a3b8', padding: '9px', borderRadius: '10px', fontSize: '10px', fontWeight: 900, letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' }}>
              {isFullscreen ? <Minimize size={13} /> : <Maximize size={13} />}
              {isFullscreen ? 'EXIT FULL SCREEN' : 'GO FULL SCREEN'}
            </button>
          )}
          {!isMobile && (
            <button
              onClick={() => setSidebarCollapsed(c => { const next = !c; try { localStorage.setItem('jc_sb_collapsed', next ? '1' : '0'); } catch {} return next; })}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', backgroundColor: 'transparent', border: '1px solid #1e293b', color: '#475569', padding: '8px', borderRadius: '10px', fontSize: '10px', fontWeight: 700, cursor: 'pointer' }}>
              {collapsed ? <ChevronRight size={14} /> : <><ChevronLeft size={13} /><span>Collapse</span></>}
            </button>
          )}
        </div>
      </div>
      );})()}

      {/* MAIN CONTENT */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', backgroundColor: 'var(--jc-ground)', width: '100%', height: '100%', overflow: 'hidden' }}>

        {/* HEADER — .jc-app-header carries the notch inset on phones (index.css, M-H1) */}
        <div className="jc-app-header" style={{ height: '60px', backgroundColor: '#050505', borderBottom: '1px solid #1e293b',
          /* 📱 SuperChart par header hide → TV-app jaisa FULL-SCREEN chart (sidebar mbar ke ☰ se khulta hai) */
          display: isMobile && IS_CHART_TAB(activeTab) ? 'none' : 'flex',
          alignItems: 'center', justifyContent: 'space-between', padding: isMobile ? '0 12px' : '0 32px', flexShrink: 0, zIndex: 40 }}>
          {/* minWidth:0 lets the title ELLIPSIS instead of pushing the right-hand
              actions (avatar + logout) off a phone screen — that overflow is what
              hid the logout button entirely (God 2026-09-05). */}
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px', minWidth: 0, flex: 1 }}>
            {/* Back — phone only, and only once there is somewhere to go back to */}
            {isMobile && canGoBack && (
              <button onClick={goBack} title="Back" aria-label="Back" style={{ width: '34px', height: '38px', backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, padding: 0 }}>
                <ChevronLeft size={17} color="#94a3b8" />
              </button>
            )}
            {/* Hamburger on mobile */}
            {isMobile && (
              <button onClick={() => setSidebarOpen(true)} style={{ width: '38px', height: '38px', backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
                <svg width="18" height="14" viewBox="0 0 18 14" fill="none">
                  <rect width="18" height="2" rx="1" fill="#94a3b8"/>
                  <rect y="6" width="13" height="2" rx="1" fill="#94a3b8"/>
                  <rect y="12" width="18" height="2" rx="1" fill="#94a3b8"/>
                </svg>
              </button>
            )}
            {/* Flexes and ellipsises rather than reserving a fixed 160px, so a long
                page name can never squeeze the logout button off the screen. */}
            <h2 style={{ fontSize: isMobile ? '13px' : '20px', fontWeight: 900, letterSpacing: isMobile ? '1px' : '2px', color: 'white', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flexShrink: 1 }}>{activeMenuTitle}</h2>
            {userRole === 'ADMIN' && !isMobile && (
              <span style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.2)', color: '#34d399', padding: '4px 12px', borderRadius: '99px', fontSize: '10px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '1px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ width: '6px', height: '6px', backgroundColor: '#10b981', borderRadius: '50%' }}></span> System Optimal
              </span>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '24px' }}>
            {/* Smart pills: NSE market state + DHAN feed — desktop only */}
            {!isMobile && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {mktInfo.exchanges.map((ex: any) => (
                  <div key={ex.name} title={`${ex.name} — ${ex.state}${ex.holiday ? ' · ' + ex.holiday : ''} (multi-exchange state machine: NSE pre-open 09:00, cash 15:30, F&O ext 15:40 · MCX till 23:30/23:55)`}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 8,
                    background: `${ex.color}14`,
                    border: `1px solid ${ex.color}4d` }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: ex.color, display: 'inline-block',
                      animation: ex.open ? 'pulse 2s infinite' : 'none' }}/>
                    <span style={{ fontSize: 9, fontWeight: 900, color: ex.color, letterSpacing: 1 }}>{ex.name} {ex.txt}</span>
                  </div>
                ))}
                {(['NIFTY_50', 'BANK_NIFTY', 'FIN_NIFTY', 'MIDCPNIFTY'] as const).map(k => {
                  const px = livePx?.idx?.[k];
                  const prev = prevPxRef.current?.[k];
                  const up = px != null && prev != null && px !== prev ? px > prev : null;
                  const col = !livePx?.fresh || px == null ? '#6b7280' : up === null ? '#94a3b8' : up ? '#10b981' : '#ef4444';
                  const label = k === 'NIFTY_50' ? 'NIFTY' : k === 'BANK_NIFTY' ? 'BANKNIFTY' : k === 'FIN_NIFTY' ? 'FINNIFTY' : 'MIDCPNIFTY';
                  return (
                    <div key={k} title={`${label} — live from the spine Redis mirror (10s TTL). '—' means the feed is not flowing; a stale price is never shown as live.`}
                      style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 8,
                        background: `${col}14`, border: `1px solid ${col}4d` }}>
                      <span style={{ fontSize: 9, fontWeight: 900, color: '#94a3b8', letterSpacing: 1 }}>{label}</span>
                      <span style={{ fontSize: 10, fontWeight: 900, color: col, letterSpacing: 0.5 }}>
                        {livePx?.fresh && px != null ? px.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                      </span>
                    </div>
                  );
                })}
                <div title="Dhan live feed status" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 11px', borderRadius: 8,
                  background: dhanFeed?.flowing ? 'rgba(56,189,248,.07)' : 'rgba(107,114,128,.07)',
                  border: `1px solid ${dhanFeed?.flowing ? 'rgba(56,189,248,.3)' : 'rgba(107,114,128,.2)'}` }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: dhanFeed?.flowing ? '#38bdf8' : '#6b7280', display: 'inline-block' }}/>
                  <span style={{ fontSize: 9, fontWeight: 900, color: dhanFeed?.flowing ? '#38bdf8' : '#6b7280', letterSpacing: 1 }}>
                    DHAN {dhanFeed ? (dhanFeed.flowing ? 'LIVE' : dhanFeed.state) : '…'}
                  </span>
                </div>
              </div>
            )}
            {/* Live Clock — desktop only */}
            {!isMobile && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingRight: '24px', borderRight: '1px solid #1e293b' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '10px', backgroundColor: '#0f172a', border: '1px solid #1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Clock size={14} color="#64748b"/>
                </div>
                <LiveClock />
              </div>
            )}

            {/* User Profile */}
            {/* flexShrink:0 — this block holds the LOGOUT button, so it must never
                be the thing that gets squeezed out. The mobile role pill that used
                to sit here has moved into the sidebar footer: on a 360px phone it
                pushed logout past the right edge, which is exactly why God could
                not find it (God 2026-09-05). */}
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '16px', flexShrink: 0 }}>
              {!isMobile && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <span style={{ fontSize: '12px', fontWeight: 900, color: 'white', textTransform: 'uppercase', letterSpacing: '1px' }}>{profile.name}</span>
                  <span style={{ fontSize: '9px', fontWeight: 900, color: userRole === 'ADMIN' ? '#818cf8' : '#60a5fa', textTransform: 'uppercase', letterSpacing: '1px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <ShieldCheck size={10} /> {profile.roleDisplay}
                  </span>
                </div>
              )}
              {/* Avatar — click karke apni photo lagao (localStorage, sab pages par) */}
              <label title="Profile photo badlo (click)" style={{ cursor: 'pointer', display: 'flex' }}>
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={onAvatarPick} />
                <img src={avatarUrl || profile.avatar} alt="User" style={{ width: isMobile ? '34px' : '40px', height: isMobile ? '34px' : '40px', borderRadius: '10px', border: '1px solid #1e293b', objectFit: 'cover' }} />
              </label>
            </div>

            {/* Header Actions */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {!isMobile && (
                <button onClick={() => setShowProfileSettings(true)} style={{ width: '38px', height: '38px', backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                  <Settings size={15} color="#64748b" />
                </button>
              )}
              <button onClick={handleLogout} style={{ width: '38px', height: '38px', backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <Power size={15} color="#64748b" />
              </button>
            </div>
          </div>
        </div>

        {/* DYNAMIC CONTENT VIEWS */}
        <div className="app-content" style={{ flex: 1, position: 'relative',
          // SuperChart/OrderLine are self-managed full-viewport canvases → keep locked; every other
          // tab (Dashboard, R&D Analytics, …) scrolls vertically so bottom content stays reachable.
          overflowX: 'hidden',
          overflowY: (IS_CHART_TAB(activeTab) || activeTab === 'OrderLine') ? 'hidden' : 'auto',
          paddingBottom: isMobile && !IS_CHART_TAB(activeTab) && activeTab !== 'OrderLine' ? 'calc(56px + env(safe-area-inset-bottom))' : 0 }}>
           {/* One Suspense boundary for the whole switch: only one tab is ever
               mounted, so a per-page boundary would buy nothing and add noise.
               The fallback deliberately does NOT blank the frame — it keeps the
               page background so a tab change reads as a load, not a crash. */}
           <React.Suspense fallback={
             <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
               height: '100%', minHeight: 220, color: '#5b7689', fontSize: 12,
               letterSpacing: 1, fontWeight: 700 }}>
               LOADING…
             </div>
           }>
           {!canOpen(activeTab) ? (
             <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
               height: '100%', minHeight: 260, gap: 10, color: '#94a3b8', textAlign: 'center', padding: 24 }}>
               <div style={{ fontSize: 34 }}>🔒</div>
               <div style={{ fontSize: 15, fontWeight: 800, color: '#e2e8f0' }}>Is page ki permission nahi hai</div>
               <div style={{ fontSize: 12, maxWidth: 420 }}>
                 {me && !me.permissions && !_isMaster ? 'Permission load ho rahi hai…'
                   : 'Master Admin se User Control me ye page allow karwaiye. Aapko sirf wahi dikhta hai jo aapke role aur permission me hai.'}
               </div>
             </div>
           ) : (<>
           {activeTab === 'Dashboard' && <Dashboard onNavigate={dashboardNav} />}
           {activeTab === 'AccountsAdmin' && userRole === 'ADMIN' && <AccountsAdmin />}
           {activeTab === 'MamtaScreener' && userRole !== 'CLIENT' && <MamtaScreener onOpenChart={handleOpenChart} />}
           {activeTab === 'SuperChart' && <SuperChart key={chartKey} initialSymbol={chartParams.symbol} initialModel={chartParams.model} initialTf={chartParams.tf} />}
           {activeTab === 'KaliSuperChart' && userRole === 'ADMIN' && (
             <SuperChart key={`kali-${chartKey}`} lane="kali"
               initialSymbol={chartParams.symbol} initialModel={chartParams.model} initialTf={chartParams.tf} />
           )}
           {activeTab === 'Watchlist' && <Watchlist onOpenChart={handleOpenChart} />}
           {activeTab === 'OptionsChain' && userRole !== 'CLIENT' && <OptionsChain onOpenChart={handleOpenChart} />}
           {activeTab === 'ApiManagement' && userRole === 'ADMIN' && <ApiManagement />}
           {activeTab === 'LiveAutoTrading' && userRole !== 'CLIENT' && <LiveTrading onOpenChart={handleOpenChart} />}
           {/* KALI is the SAME page as DUMABATI, bound to the live lane — not a
               second copy. A duplicated 6,900-line page would drift from this one
               within a week and the two would disagree about the numbers. */}
           {activeTab === 'Kali' && userRole === 'ADMIN' && <LiveTrading lane="kali" onOpenChart={handleOpenChart} />}
           {activeTab === 'Pipeline' && userRole === 'ADMIN' && <PipelinePanel />}
           {activeTab === 'PaperTrading' && userRole !== 'CLIENT' && <PaperTrading />}
           {activeTab === 'PnlReports' && <PnLReports />}
           {activeTab === 'RnDAnalytics' && userRole !== 'CLIENT' && <RnDAnalytics />}
           {activeTab === 'MoneyManagement' && <MoneyManagement />}
           {activeTab === 'ClientPortfolio' && userRole !== 'CLIENT' && <ClientPortfolio />}
           {activeTab === 'SymbolManager' && userRole === 'ADMIN' && <SymbolManager />}
           {activeTab === 'UserControl' && userRole === 'ADMIN' && <UserControl />}
           {activeTab === 'MamtaAiPro' && userRole === 'ADMIN' && <MamtaAiv6 isOpen={true} onClose={() => setActiveTab('Dashboard')} />}
           {activeTab === 'OrderLine' && <OrderLine />}
           {activeTab === 'PositionBook' && <PositionBook />}
           </>)}
           </React.Suspense>
        </div>
      </div>
    </div>
  );
}